/**
 * Who is signed in. Until /api/auth/me answers, only sign-in, an invitation link, About and Privacy are shown;
 * the server refuses every other call anyway. A 401 from any call returns to sign-in; the session is looked at
 * again when the window regains focus (Gap Assessor's App.tsx @ 2023d32).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

import { api, ApiError, SIGNED_OUT_EVENT } from './api'

export interface Person {
  id: string
  email: string
  displayName: string
  language: 'en' | 'sv'
  firmRoles: string[]
}

export interface ResetNotice {
  at: string
  requestedBy: string | null
  confirmedBy: string | null
  via: 'person' | 'host_command'
}

interface MeResponse {
  person: Person
  resetNotice: ResetNotice | null
}

export interface SessionValue {
  person: Person
  resetNotice: ResetNotice | null
  refresh: () => Promise<void>
  signOut: () => Promise<void>
  acknowledgeReset: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

export function useSession(): SessionValue {
  const s = useContext(SessionContext)
  if (!s) throw new Error('useSession outside a signed-in page')
  return s
}

export function useSessionState() {
  const [me, setMe] = useState<MeResponse | null>(null)
  const [checked, setChecked] = useState(false)
  const [unreachable, setUnreachable] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setMe(await api.get<MeResponse>('auth/me'))
      setUnreachable(false)
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setMe(null)
      else {
        // What went wrong is for the console; the page says the server could not be reached (batch O-D).
        console.warn('[olivia] could not check the session:', e instanceof Error ? e.message : String(e))
        setUnreachable(true)
      }
    } finally {
      setChecked(true)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    const ended = () => setMe(null)
    window.addEventListener(SIGNED_OUT_EVENT, ended)
    return () => window.removeEventListener(SIGNED_OUT_EVENT, ended)
  }, [])

  useEffect(() => {
    const again = () => {
      if (me) void refresh()
    }
    window.addEventListener('focus', again)
    return () => window.removeEventListener('focus', again)
  }, [me, refresh])

  const value = useMemo<SessionValue | null>(
    () =>
      me && {
        person: me.person,
        resetNotice: me.resetNotice,
        refresh,
        signOut: async () => {
          try {
            await api.post('auth/logout')
          } catch {
            // The cookie is HttpOnly; if the call fails the server keeps the session until it expires.
          }
          setMe(null)
        },
        acknowledgeReset: async () => {
          await api.post('auth/reset-notice/ack')
          await refresh()
        },
      },
    [me, refresh],
  )
  return { value, checked, unreachable, refresh }
}

export function SessionProvider({ value, children }: { value: SessionValue; children: ReactNode }) {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}
